/**
 * Upstream Akash message encoders for the tenant surface.
 *
 * Field numbers here are NOT from memory or from upstream documentation. They come
 * from `schema.json`, which `tools/protoschema.py` extracts from the `.pb.go`
 * struct tags of the exact `pkg.akt.dev/go` version this chain is built from — and
 * `messages.test.ts` encodes each message, reads the raw protobuf tags back off the
 * wire, and asserts they match that schema. A field number that drifts fails the
 * build instead of producing a transaction the chain rejects at signing time.
 *
 * That machinery is not ceremony. Extracting rather than assuming caught five
 * things in one sitting:
 *
 *   - `MsgDepositDeployment` does not exist in deployment v1beta4. It was there in
 *     v1beta3; escrow top-up is now `akash.escrow.v1.MsgAccountDeposit`.
 *   - `MsgUpdateDeployment` skips field number 2 — it is `id=1, hash=3`.
 *   - `BidID` has a sixth field, `bseq`.
 *   - `MsgCloseLease` has a `reason` field.
 *   - `ResourceUnit` embeds `akash.base.resources.v1beta4.Resources` at field 1,
 *     not the similarly-named `akash.base.v1beta3.Resources`.
 *
 * Every one of those would have been a plausible hand-written mistake, and every
 * one fails only at signing.
 */

import type { GeneratedType } from '@cosmjs/proto-signing'
import { BinaryReader, BinaryWriter } from 'cosmjs-types/binary'

// ── akash.market.v1.BidID ────────────────────────────────────────────────────

export interface BidID {
  owner: string
  dseq: bigint
  gseq: number
  oseq: number
  provider: string
  /** Bid sequence. Present in the pinned module and easy to miss. */
  bseq: number
}

export const BidID = {
  typeUrl: '/akash.market.v1.BidID',

  encode(m: BidID, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    if (m.owner !== '') w.uint32(10).string(m.owner)
    if (m.dseq !== 0n) w.uint32(16).uint64(m.dseq)
    if (m.gseq !== 0) w.uint32(24).uint32(m.gseq)
    if (m.oseq !== 0) w.uint32(32).uint32(m.oseq)
    if (m.provider !== '') w.uint32(42).string(m.provider)
    if (m.bseq !== 0) w.uint32(48).uint32(m.bseq)
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): BidID {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = BidID.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.owner = r.string()
          break
        case 2:
          m.dseq = r.uint64()
          break
        case 3:
          m.gseq = r.uint32()
          break
        case 4:
          m.oseq = r.uint32()
          break
        case 5:
          m.provider = r.string()
          break
        case 6:
          m.bseq = r.uint32()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: Partial<BidID>): BidID {
    return {
      owner: o.owner ?? '',
      dseq: o.dseq ?? 0n,
      gseq: o.gseq ?? 0,
      oseq: o.oseq ?? 0,
      provider: o.provider ?? '',
      bseq: o.bseq ?? 0,
    }
  },
}

// ── akash.deployment.v1.DeploymentID ─────────────────────────────────────────

export interface DeploymentID {
  owner: string
  dseq: bigint
}

export const DeploymentID = {
  typeUrl: '/akash.deployment.v1.DeploymentID',

  encode(m: DeploymentID, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    if (m.owner !== '') w.uint32(10).string(m.owner)
    if (m.dseq !== 0n) w.uint32(16).uint64(m.dseq)
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): DeploymentID {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = DeploymentID.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.owner = r.string()
          break
        case 2:
          m.dseq = r.uint64()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: Partial<DeploymentID>): DeploymentID {
    return { owner: o.owner ?? '', dseq: o.dseq ?? 0n }
  },
}

// ── akash.market.v1beta5.MsgCreateLease ──────────────────────────────────────
//
// v1beta5, not v1beta4. Both define MsgCreateLease; v1beta5 is the version that
// registers the Msg service, and a v1beta4 type URL is one the chain does not route.

export interface MsgCreateLease {
  bidId: BidID
}

export const MsgCreateLease = {
  typeUrl: '/akash.market.v1beta5.MsgCreateLease',

  encode(m: MsgCreateLease, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    BidID.encode(m.bidId, w.uint32(10).fork()).ldelim()
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): MsgCreateLease {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = MsgCreateLease.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.bidId = BidID.decode(r, r.uint32())
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  // Nested partials, not Partial<MsgCreateLease>: callers build a BidID field by
  // field, and requiring a complete child defeats the point of fromPartial.
  fromPartial(o: { bidId?: Partial<BidID> }): MsgCreateLease {
    return { bidId: BidID.fromPartial(o.bidId ?? {}) }
  },
}

// ── akash.market.v1beta5.MsgCloseLease ───────────────────────────────────────

export interface MsgCloseLease {
  id: BidID
  /** Present in the pinned module; omitted encodes as the zero value. */
  reason: number
}

export const MsgCloseLease = {
  typeUrl: '/akash.market.v1beta5.MsgCloseLease',

  encode(m: MsgCloseLease, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    BidID.encode(m.id, w.uint32(10).fork()).ldelim()
    if (m.reason !== 0) w.uint32(16).uint32(m.reason)
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): MsgCloseLease {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = MsgCloseLease.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.id = BidID.decode(r, r.uint32())
          break
        case 2:
          m.reason = r.uint32()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: { id?: Partial<BidID>; reason?: number }): MsgCloseLease {
    return { id: BidID.fromPartial(o.id ?? {}), reason: o.reason ?? 0 }
  },
}

// ── akash.deployment.v1beta4.MsgCloseDeployment ──────────────────────────────

export interface MsgCloseDeployment {
  id: DeploymentID
}

export const MsgCloseDeployment = {
  typeUrl: '/akash.deployment.v1beta4.MsgCloseDeployment',

  encode(m: MsgCloseDeployment, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    DeploymentID.encode(m.id, w.uint32(10).fork()).ldelim()
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): MsgCloseDeployment {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = MsgCloseDeployment.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.id = DeploymentID.decode(r, r.uint32())
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: { id?: Partial<DeploymentID> }): MsgCloseDeployment {
    return { id: DeploymentID.fromPartial(o.id ?? {}) }
  },
}

// ── akash.deployment.v1beta4.MsgUpdateDeployment ─────────────────────────────
//
// `id=1, hash=3`: field 2 is skipped, and an encoder renumbering to 1,2 would be
// silently wrong. Checked against the pinned module's struct tags —
// ddws-go/node/deployment/v1beta4/deploymentmsg.pb.go:140-145 — through
// schema.json, and against bytes from its own `Marshal()` (update-vectors.json,
// tools/updateref).
//
// The message carries no groups. An update changes only the manifest version the
// chain records (pkg.akt.dev/node@v1.1.1 x/deployment/handler/server.go:95-120,
// which also refuses a closed deployment and a hash equal to the current one); the
// resources, counts and prices stay those the deployment was created with.

/** The manifest version is a SHA-256: `v1.HashLength`, enforced by `ValidateBasic`. */
export const DEPLOYMENT_HASH_LENGTH = 32

export interface MsgUpdateDeployment {
  id: DeploymentID
  /** The new manifest version — 32 bytes from `manifestVersion()`. */
  hash: Uint8Array
}

export const MsgUpdateDeployment = {
  typeUrl: '/akash.deployment.v1beta4.MsgUpdateDeployment',

  encode(m: MsgUpdateDeployment, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    DeploymentID.encode(m.id, w.uint32(10).fork()).ldelim()
    if (m.hash.length !== 0) w.uint32(26).bytes(m.hash)
    return w
  },

  decode(input: BinaryReader | Uint8Array, length?: number): MsgUpdateDeployment {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = MsgUpdateDeployment.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.id = DeploymentID.decode(r, r.uint32())
          break
        case 3:
          m.hash = r.bytes()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: { id?: Partial<DeploymentID>; hash?: Uint8Array }): MsgUpdateDeployment {
    return { id: DeploymentID.fromPartial(o.id ?? {}), hash: o.hash ?? new Uint8Array() }
  },
}

/**
 * The messages defined in THIS file. Not the whole set the console registers —
 * `MsgCreateDeployment` lives in `deployment.ts`, because it carries the entire
 * `GroupSpec` subtree and is asserted differently: the messages here are checked
 * against `schema.json`'s field numbers, that one against bytes emitted by the real
 * Go `Marshal()`.
 *
 * Register `protocolRegistryTypes` from `./registry`, which composes both. Importing
 * this list directly gets you a registry with no way to create a deployment.
 */
export const messageRegistryTypes: ReadonlyArray<[string, GeneratedType]> = [
  [MsgCreateLease.typeUrl, MsgCreateLease as unknown as GeneratedType],
  [MsgCloseLease.typeUrl, MsgCloseLease as unknown as GeneratedType],
  [MsgCloseDeployment.typeUrl, MsgCloseDeployment as unknown as GeneratedType],
  [MsgUpdateDeployment.typeUrl, MsgUpdateDeployment as unknown as GeneratedType],
]

/** Build the message a tenant sends to accept a bid — the rent-capacity moment. */
export function createLeaseMessage(bid: Partial<BidID>) {
  return { typeUrl: MsgCreateLease.typeUrl, value: MsgCreateLease.fromPartial({ bidId: bid }) }
}

export function closeDeploymentMessage(owner: string, dseq: bigint) {
  return {
    typeUrl: MsgCloseDeployment.typeUrl,
    value: MsgCloseDeployment.fromPartial({ id: { owner, dseq } }),
  }
}

/**
 * Build the message that points a running deployment at a new manifest version.
 *
 * Refuses a hash of the wrong length here, with the chain's own reason, rather
 * than letting the wallet sign a transaction `ValidateBasic` rejects.
 */
export function updateDeploymentMessage(owner: string, dseq: bigint, hash: Uint8Array) {
  if (hash.length !== DEPLOYMENT_HASH_LENGTH) {
    throw new Error(hash.length === 0 ? 'Invalid: empty hash' : 'Invalid: deployment hash')
  }
  return {
    typeUrl: MsgUpdateDeployment.typeUrl,
    value: MsgUpdateDeployment.fromPartial({ id: { owner, dseq }, hash }),
  }
}
